import { Typography } from '@/components/Typography/Typography';
import { ChipWrapper } from './styles';
import { Box } from '@/components/Box/Box';
import { ComponentPropsWithoutRef, ReactNode } from 'react';
import { InputError } from '../InputError/InputError';

export interface ChipsOptionType {
  value: string | boolean;
  label: string;
}

type ChipType = {
  isActive: boolean;
  onSelect: (value: string | boolean) => void;
  option: ChipsOptionType;
  disabled?: boolean;
};

interface ChipsType extends Omit<ComponentPropsWithoutRef<'div'>, 'onSelect'> {
  label: ReactNode;
  onSelect: (value: string | boolean) => void;
  disabled?: boolean;
  value: string;
  options: ChipsOptionType[];
  direction?: 'column' | 'row';
  error?: string;
  apiError?: string;
  hasErrorComponent?: boolean;
}

const Chip = ({ isActive, onSelect, option, disabled }: ChipType) => {
  const { value, label } = option || {};

  return (
    <ChipWrapper isActive={isActive} disabled={disabled} onClick={() => onSelect?.(value)}>
      {label}
    </ChipWrapper>
  );
};

export const Chips = ({
  label,
  options,
  value,
  disabled,
  onSelect,
  direction = 'row',
  error,
  apiError,
  hasErrorComponent = true,
  ...restProps
}: ChipsType) => {
  const boxStyles = direction === 'row' ? { alignItems: 'center' } : { alignItems: 'flex-start' };

  return (
    <>
      <Box display='flex' columnGap='16px' rowGap='6px' flexDirection={direction} {...boxStyles} {...restProps}>
        <Typography variant='bodySmall'>{label}</Typography>
        <Box display='flex' gap='10px'>
          {options.map((option) => {
            const isSelected = value === option.value && !disabled;

            return (
              <Chip key={option?.label} option={option} isActive={isSelected} disabled={disabled} onSelect={onSelect} />
            );
          })}
        </Box>
      </Box>
      {hasErrorComponent && (
        <Box display='flex'>
          <InputError error={error} apiError={apiError} />
        </Box>
      )}
    </>
  );
};
